import { Home, Server, ChevronRight, ArrowLeft, Layers, Wrench, ChevronLeft, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { rackLabel } from '../../utils/rackName.logic';
import {
  normalizeRackStatus,
  normalizeRackRole,
  RACK_STATUS_METAS,
  RACK_ROLE_METAS,
} from '../../utils/rackLifecycle.logic';

interface RackConfig {
  id: number;
  row_index: number;
  rack_index: number;
  name?: string;
  label?: string | null;
  u_slots?: number | null;
  max_weight_kg?: number;
  max_power_w?: number;
  type?: string;
}

interface RackItem {
  id: number;
  row_index: number;
  rack_index: number;
  name?: string;
  label?: string | null;
  u_slots?: number | null;
}

interface RackEquipmentItem {
  id: string;
  name: string;
  equipment_type: string;
  u_height: number;
  u_position: number;
  weight_kg?: number;
  power_w?: number;
  is_zero_u?: boolean;
}

import type { SpaceMetrics } from '../../utils/rackReservation.logic';

interface CompactRackHeaderProps {
  datacenterCode?: string;
  datacenterName?: string;
  rack: RackConfig | null;
  racksList?: RackItem[];           // все стойки ЦОДа для дропдауна по ряду
  equipmentList: RackEquipmentItem[];
  spaceMetrics?: SpaceMetrics;
  projectionMode: 'front' | 'rear' | 'split';
  setProjectionMode: (mode: 'front' | 'rear' | 'split') => void;
  isMountingMode: boolean;
  setIsMountingMode: (val: boolean) => void;
  onBack: () => void;
  onEditRack?: () => void;
  onAddEquipment?: () => void;
  theme?: 'dark' | 'light';
  prevRack?: { id: number; row_index: number; rack_index: number; label?: string | null; name?: string } | null;
  nextRack?: { id: number; row_index: number; rack_index: number; label?: string | null; name?: string } | null;
  onNavigateRack?: (rackId: number) => void;
}

export function CompactRackHeader({
  datacenterCode,
  datacenterName,
  rack,
  racksList = [],
  equipmentList,
  spaceMetrics,
  projectionMode,
  setProjectionMode,
  isMountingMode,
  setIsMountingMode,
  onBack,
  onEditRack,
  theme = 'dark',
  prevRack,
  nextRack,
  onNavigateRack,
}: CompactRackHeaderProps) {
  const navigate = useNavigate();
  const [rowDropOpen, setRowDropOpen] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) {
        setRowDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Group racksList by row for dropdown
  const racksInCurrentRow = racksList
    .filter(r => r.row_index === rack?.row_index)
    .sort((a, b) => (a.rack_index ?? 0) - (b.rack_index ?? 0));

  const isMotionOk = typeof window !== 'undefined'
    ? !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : true;

  if (!rack) return null;

  const totalU = rack.u_slots || 42;
  const usedU = equipmentList.filter(eq => !eq.is_zero_u).reduce((acc, eq) => acc + (eq.u_height || 1), 0);
  const uPercentage = Math.min(Math.round((usedU / totalU) * 100), 100);

  const currentWeight = equipmentList.reduce((acc, eq) => acc + (eq.weight_kg || 0), 0);
  const currentPower = equipmentList.reduce((acc, eq) => acc + (eq.power_w || 0), 0);

  const isDark = theme === 'dark';

  return (
    <div className={`h-[38px] min-h-[38px] border-b px-3 flex items-center gap-2 font-mono text-xs z-30 shrink-0 select-none overflow-hidden ${
      isDark ? 'bg-slate-950/90 border-slate-800 text-slate-300' : 'bg-white border-gray-200 text-slate-700'
    }`}>
      
      {/* Breadcrumb Navigation — min-w-0 allows it to shrink */}
      <div className={`flex items-center gap-1.5 min-w-0 flex-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
        <button 
          onClick={onBack}
          className={`p-1 rounded text-slate-400 hover:text-cyan-400 transition shrink-0 ${isDark ? 'hover:bg-slate-800' : 'hover:bg-gray-100'}`}
          aria-label="Назад"
        >
          <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
        </button>

        {/* Home */}
        <button 
          onClick={() => navigate('/')}
          className={`flex items-center gap-1 hover:text-cyan-400 transition shrink-0 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
          aria-label="Главная"
        >
          <Home className="w-3 h-3" aria-hidden="true" />
        </button>

        <ChevronRight className="w-3 h-3 text-slate-600 shrink-0" />

        {/* Имя ЦОДа */}
        <button 
          onClick={() => navigate(`/${datacenterCode}/racks`)}
          className={`text-[11px] transition shrink-0 hover:text-cyan-400 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
          title="Список стоек"
        >
          {datacenterName || datacenterCode?.toUpperCase()}
        </button>

        <ChevronRight className={`w-3 h-3 shrink-0 ${isDark ? 'text-slate-600' : 'text-gray-400'}`} />

        {/* Ряд X — дропдаун со стойками ряда */}
        <div ref={dropRef} className="relative shrink-0">
          <button
            onClick={() => setRowDropOpen(o => !o)}
            className={`flex items-center gap-1 text-[11px] transition uppercase tracking-wider hover:text-cyan-400 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
            title="Выбрать стойку в этом ряду"
          >
            Ряд {(rack.row_index ?? 0) + 1}
            <ChevronDown className="w-3 h-3" />
          </button>

          {rowDropOpen && (
            <div className={`absolute top-full left-0 mt-1 z-50 rounded-lg border shadow-xl min-w-[160px] py-1 ${
              isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'
            }`}>
              <div className={`px-3 py-1 text-[9px] uppercase tracking-wider mb-1 ${
                isDark ? 'text-slate-500 border-b border-slate-800' : 'text-gray-400 border-b border-gray-100'
              }`}>
                Ряд {(rack.row_index ?? 0) + 1} — стойки
              </div>
              {racksInCurrentRow.length === 0 && (
                <div className="px-3 py-2 text-[11px] text-slate-500">Нет стоек</div>
              )}
              {racksInCurrentRow.map(r => (
                <button
                  key={r.id}
                  onClick={() => { setRowDropOpen(false); onNavigateRack?.(r.id); }}
                  className={`w-full text-left px-3 py-1.5 text-[11px] flex items-center gap-2 transition ${
                    String(r.id) === String(rack.id)
                      ? isDark ? 'bg-cyan-900/40 text-cyan-300' : 'bg-cyan-50 text-cyan-700'
                      : isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-700 hover:bg-gray-50'
                  }`}
                >
                  <Server className="w-3 h-3 shrink-0" />
                  {rackLabel(r)}
                  {String(r.id) === String(rack.id) && (
                    <span className="ml-auto text-[9px] text-cyan-400">← здесь</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <ChevronRight className={`w-3 h-3 shrink-0 ${isDark ? 'text-slate-600' : 'text-gray-400'}`} />

        {/* Текущая стойка */}
        <div className={`flex items-center gap-1 font-bold text-[11px] min-w-0 ${isDark ? 'text-slate-100' : 'text-slate-800'}`}>
          <Server className="w-3 h-3 text-cyan-400 shrink-0" />
          <span className="truncate">{rackLabel(rack)}</span>
        </div>

        {/* Status & Role Badges */}
        {(() => {
          const status = normalizeRackStatus((rack as any)?.status);
          const role = normalizeRackRole((rack as any)?.role, rack?.type);
          const statusMeta = RACK_STATUS_METAS[status];
          const roleMeta = RACK_ROLE_METAS[role];
          return (
            <div className="flex items-center gap-1 shrink-0 ml-1">
              <span
                className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider shrink-0"
                style={{
                  background: statusMeta.badgeBg,
                  color: statusMeta.color,
                  border: `1px solid ${statusMeta.badgeBorder}`,
                }}
                title={`Статус: ${statusMeta.labelRu} (${statusMeta.description})`}
              >
                {statusMeta.labelRu}
              </span>
              <span
                className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider shrink-0 bg-slate-800 text-slate-300 border border-slate-700"
                title={`Роль: ${roleMeta.labelRu} (${roleMeta.description})`}
              >
                {roleMeta.shortCode}
              </span>
              {(rack as any)?.tenant && (rack as any)?.tenant !== 'mine' && (
                <span
                  data-testid="header-rack-tenant-badge"
                  className="px-1.5 py-0.5 rounded text-[9px] font-bold font-mono tracking-wider shrink-0 bg-purple-950/80 text-purple-300 border border-purple-600/50"
                  title={`Владелец стойки: ${(rack as any)?.tenant}`}
                >
                  {(rack as any)?.tenant}
                </span>
              )}
            </div>
          );
        })()}
      </div>

      {/* Center Micro Telemetry Badges — hidden below 1536px */}
      <div className="hidden 2xl:flex items-center gap-3 text-[10px]">
        <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded border ${
          isDark ? 'bg-slate-900/80 border-slate-800 text-slate-300' : 'bg-gray-100 border-gray-200 text-slate-600'
        }`}>
          <span className={isDark ? 'text-slate-500' : 'text-gray-400'}>ЮНИТЫ:</span>
          <span className="font-bold text-cyan-500 tabular-nums">
            {spaceMetrics ? spaceMetrics.occupiedU : usedU}U ЗАН
          </span>
          {spaceMetrics && spaceMetrics.reservedU > 0 && (
            <>
              <span className="text-slate-600">·</span>
              <span className="font-bold text-amber-400 tabular-nums">
                {spaceMetrics.reservedU}U РЕЗ
              </span>
            </>
          )}
          <span className="text-slate-600">·</span>
          <span className={`tabular-nums ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
            {spaceMetrics ? spaceMetrics.availableU : totalU - usedU}U СВОБ
          </span>
          <span className={`font-bold tabular-nums ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>
            ({spaceMetrics ? spaceMetrics.utilizationPct : uPercentage}%)
          </span>
        </div>

        <div className={`flex items-center gap-1 px-2 py-0.5 rounded border ${
          isDark ? 'bg-slate-900/80 border-slate-800 text-slate-300' : 'bg-gray-100 border-gray-200 text-slate-600'
        }`}>
          <span className={isDark ? 'text-slate-500' : 'text-gray-400'}>МОЩНОСТЬ:</span>
          <span className="font-bold text-amber-400 tabular-nums">{currentPower} Вт</span>
        </div>

        <div className={`flex items-center gap-1 px-2 py-0.5 rounded border ${
          isDark ? 'bg-slate-900/80 border-slate-800 text-slate-300' : 'bg-gray-100 border-gray-200 text-slate-600'
        }`}>
          <span className={isDark ? 'text-slate-500' : 'text-gray-400'}>МАССА:</span>
          <span className="font-bold text-emerald-500 tabular-nums">{currentWeight} кг</span>
        </div>
      </div>

      {/* Projection View Controls & Mounting Toggle — shrink-0 so they never clip */}
      <div className="flex items-center gap-1.5 shrink-0">
        
        {/* Projection Selector */}
        <div className={`flex p-0.5 rounded border text-[10px] ${
          isDark ? 'bg-slate-900 border-slate-800' : 'bg-gray-100 border-gray-200'
        }`}>
          <button
            onClick={() => setProjectionMode('split')}
            className={`px-1.5 py-0.5 rounded transition whitespace-nowrap ${
              projectionMode === 'split' 
                ? 'bg-cyan-600 text-white font-bold shadow' 
                : isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-800'
            }`}
            title="Фронт и тыл одновременно"
            aria-pressed={projectionMode === 'split'}
          >
            Split
          </button>
          <button
            onClick={() => setProjectionMode('front')}
            className={`px-1.5 py-0.5 rounded transition whitespace-nowrap ${
              projectionMode === 'front' 
                ? 'bg-cyan-600 text-white font-bold shadow' 
                : isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-800'
            }`}
            title="Фронтальная панель"
          >
            Фронт
          </button>
          <button
            onClick={() => setProjectionMode('rear')}
            className={`px-1.5 py-0.5 rounded transition whitespace-nowrap ${
              projectionMode === 'rear' 
                ? 'bg-cyan-600 text-white font-bold shadow' 
                : isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-800'
            }`}
            title="Задняя панель"
          >
            Тыл
          </button>
        </div>

        {/* Mounting Mode Toggle */}
        <button
          onClick={() => setIsMountingMode(!isMountingMode)}
          aria-pressed={isMountingMode}
          aria-label={isMountingMode ? 'Режим монтажа активен' : 'Режим монтажа выключен'}
          className={`px-1.5 py-0.5 rounded text-[10px] font-bold border transition flex items-center gap-1 whitespace-nowrap ${
            isMountingMode
              ? `bg-amber-500/20 border-amber-500/60 text-amber-300 ${isMotionOk ? 'animate-pulse' : 'ring-2 ring-amber-500/60'}`
              : isDark
                ? 'bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200'
                : 'bg-gray-100 border-gray-200 text-gray-500 hover:text-gray-800'
          }`}
          title="Режим перетаскивания и установки приборов мышью"
        >
          <Layers className="w-3 h-3" aria-hidden="true" />
          {isMountingMode ? 'Вкл' : 'Монтаж'}
        </button>

        {onEditRack && (
          <button
            onClick={onEditRack}
            className={`p-1 rounded transition text-slate-400 hover:text-cyan-400 ${
              isDark ? 'hover:bg-slate-800' : 'hover:bg-gray-100'
            }`}
            title="Параметры стойки"
          >
            <Wrench className="w-3.5 h-3.5" />
          </button>
        )}

        {/* ── Rack Row Navigation ── */}
        {(prevRack || nextRack) && (
          <div className={`flex items-center border rounded text-[10px] font-mono overflow-hidden ${
            isDark ? 'border-slate-800' : 'border-gray-300'
          }`}>
            <button
              disabled={!prevRack}
              onClick={() => prevRack && onNavigateRack?.(prevRack.id)}
              className={`px-1.5 py-0.5 disabled:opacity-30 disabled:cursor-not-allowed transition ${
                isDark
                  ? 'text-slate-400 hover:text-cyan-300 hover:bg-slate-800'
                  : 'text-slate-500 hover:text-cyan-600 hover:bg-gray-100'
              }`}
              title={prevRack ? `← ${rackLabel(prevRack)}` : 'Первая стойка в ряду'}
            >
              <ChevronLeft className="w-3 h-3" />
            </button>
            <span className={`px-1.5 text-[9px] border-x ${
              isDark ? 'text-slate-500 border-slate-800' : 'text-gray-400 border-gray-300'
            }`}>
              {rack?.rack_index !== undefined ? `С${rack.rack_index}` : '—'}
            </span>
            <button
              disabled={!nextRack}
              onClick={() => nextRack && onNavigateRack?.(nextRack.id)}
              className={`px-1.5 py-0.5 disabled:opacity-30 disabled:cursor-not-allowed transition ${
                isDark
                  ? 'text-slate-400 hover:text-cyan-300 hover:bg-slate-800'
                  : 'text-slate-500 hover:text-cyan-600 hover:bg-gray-100'
              }`}
              title={nextRack ? `${rackLabel(nextRack)} →` : 'Последняя стойка в ряду'}
            >
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Theme toggle removed — now in global header only */}
      </div>

    </div>
  );
}
